import React from 'react';
import {
  CubeTransparentIcon,
  PlayCircleIcon,
  TrophyIcon,
  SparklesIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const GamePage = () => {
  const navigate = useNavigate();

  const games = [
    {
      id: '/memory-match',
      title: 'Memory Match',
      description: 'Sharpen your memory by finding matching pairs!',
      emoji: '🧠',
      gradient: 'from-blue-200 to-blue-400',
      bubbleColor: 'bg-blue-100',
      button: 'bg-blue-500 hover:bg-blue-600',
      isLocked: false, // UNLOCKED
    },
    {
      id: '/word-wizard',
      title: 'Word Wizard',
      description: 'Spell out words and boost your vocabulary!',
      emoji: '✍️',
      gradient: 'from-green-200 to-green-400',
      bubbleColor: 'bg-green-100',
      button: 'bg-green-500 hover:bg-green-600',
      isLocked: false, // LOCKED for demonstration
    },
    {
      id: '/TicTacToe',
      title: 'Tic Tac Toe',
      description: 'Classic Xs and Os fun!',
      emoji: '❌⭕',
      gradient: 'from-purple-200 to-purple-400',
      bubbleColor: 'bg-purple-100',
      button: 'bg-purple-500 hover:bg-purple-600',
      isLocked: false, // UNLOCKED
    },
    {
      id: '/molegame',
      title: 'Whack-A-Mole',
      description: 'Quick reflexes needed to whack those moles!',
      emoji: '🦨',
      gradient: 'from-red-200 to-red-400',
      bubbleColor: 'bg-red-100',
      button: 'bg-red-500 hover:bg-red-600',
      isLocked: false, // LOCKED for demonstration
    },
    {
      id: '/simongame',
      title: 'Simon Says',
      description: 'Follow the sequence, test your memory!',
      emoji: '🌈',
      gradient: 'from-yellow-200 to-yellow-400',
      bubbleColor: 'bg-yellow-100',
      button: 'bg-yellow-500 hover:bg-yellow-600',
      isLocked: false, // UNLOCKED
    },
    {
      id: '/snackgame',
      title: 'Snake Game',
      description: 'Grow your snake, avoid obstacles!',
      emoji: '🐍',
      gradient: 'from-teal-200 to-teal-400',
      bubbleColor: 'bg-teal-100',
      button: 'bg-teal-500 hover:bg-teal-600',
      isLocked: false, // LOCKED for demonstration
    },
    {
      id: '/math-monsters',
      title: 'Math Monsters',
      description: 'Conquer math problems!',
      emoji: '➕',
      gradient: 'from-orange-200 to-orange-400',
      bubbleColor: 'bg-orange-100',
      button: 'bg-orange-500 hover:bg-orange-600',
      isLocked: false, // UNLOCKED
    },
    {
      id: '/chess-challenge',
      title: 'Chess Challenge',
      description: 'Strategize and checkmate!',
      emoji: '♟️',
      gradient: 'from-gray-200 to-gray-400',
      bubbleColor: 'bg-gray-100',
      button: 'bg-gray-500 hover:bg-gray-600',
      isLocked: true, // LOCKED for demonstration
    },
    {
      id: '/ludo-adventure',
      title: 'Ludo Adventure',
      description: 'Roll, race, and win!',
      emoji: '🎲',
      gradient: 'from-pink-200 to-pink-400',
      bubbleColor: 'bg-pink-100',
      button: 'bg-pink-500 hover:bg-pink-600',
      isLocked: true, // UNLOCKED
    },
  ];

  const handleExploreItemClick = (gameId, isLocked) => {
    if (!isLocked) {
      navigate(gameId);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white-50 via-white-50 to-pink-50 px-4 py-16 sm:px-8 lg:px-16 font-['Nunito'] overflow-hidden">
      {/* Ensure 'Nunito' or another friendly font is imported */}

      {/* Header Section */}
  <header className="mb-10">
  <div className="relative flex  flex-col sm:flex-row">
    {/* Floating background effect */}
    <div className="absolute inset-0 flex justify-center items-center">
      <div className="bg-blue-200 rounded-full w-24 h-24 blur-3xl opacity-50 transform -translate-y-12 animate-float"></div>
    </div>

    {/* Icon on left */}
    <div className="relative z-10 flex-shrink-0 mb-4 sm:mb-0 sm:mr-6">
      <CubeTransparentIcon className="w-20 h-20 text-indigo-600 animate-bounce-in" />
    </div>

    {/* Text on right */}
    <div className="relative z-10 text-center sm:text-left">
      <h1 className="text-4xl sm:text-4xl font-extrabold leading-tight text-gray-900 drop-shadow-lg">
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-pink-600">
          Welcome to the Fun Zone!
        </span>
      </h1>
      <p className="text-xl sm:text-2xl text-gray-700 mt-2 max-w-3xl">
        Ready to play awesome games and boost your brain power? Let's go!
      </p>
    </div>
  </div>
</header>


      {/* Games Grid */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {games.map((game, index) => (
          <div
            key={game.id}
            className={`relative p-8 rounded-3xl bg-gradient-to-br ${game.gradient} shadow-xl
                       flex flex-col items-center text-center transform transition-all duration-300
                       ${game.isLocked ? 'grayscale opacity-50 cursor-not-allowed' : 'hover:scale-105 hover:rotate-1 hover:shadow-2xl cursor-pointer'}
                       animate-fade-in`}
            style={{ animationDelay: `${index * 0.08}s` }}
            onClick={() => handleExploreItemClick(game.id, game.isLocked)}
          >
            {/* Playful background bubbles */}
            {!game.isLocked && (
              <>
                <div className={`absolute -top-4 -right-4 w-20 h-20 ${game.bubbleColor} rounded-full blur-md opacity-70 group-hover:scale-125 transition-transform`}></div>
                <div className={`absolute -bottom-4 -left-4 w-16 h-16 ${game.bubbleColor} rounded-full blur-md opacity-70 group-hover:scale-125 transition-transform`}></div>
              </>
            )}

            {/* Lock Overlay for Locked Games */}
            {game.isLocked && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-opacity-80 rounded-2xl z-20">
                <LockClosedIcon className="w-18 h-18 text-black animate-pulse-slow" />
                <span className="text-black text-2xl font-extrabold mt-4 drop-shadow-lg">LOCKED</span>
              </div>
            )}

            <div className={`w-32 h-32 ${game.bubbleColor} rounded-full flex items-center justify-center text-7xl mb-6 border-4 border-white shadow-lg transform ${!game.isLocked && 'group-hover:scale-110 transition-transform'}`}>
              {game.emoji}
            </div>
            <h2 className="text-3xl font-extrabold text-white mb-3 drop-shadow-md">{game.title}</h2>
            <p className="text-white text-lg opacity-90 mb-6">{game.description}</p>
            <button
              className={`px-10 py-4 rounded-full flex items-center text-xl font-bold shadow-lg transition transform
                          ${game.isLocked ? 'bg-black cursor-not-allowed opacity-80' : `${game.button} hover:-translate-y-1 hover:shadow-2xl`}
                          text-white focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-offset-white`}
              disabled={game.isLocked}
            >
              {game.isLocked ? (
                <>
                  <LockClosedIcon className="w-7 h-7 mr-3" /> Locked
                </>
              ) : (
                <>
                  <PlayCircleIcon className="w-7 h-7 mr-3" /> Play Now!
                </>
              )}
            </button>
          </div>
        ))}
      </section>

      <div className="max-w-7xl mx-auto mt-24 grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Game Tips Section */}
        <section className="bg-white p-12 rounded-3xl shadow-2xl border-l-8 border-yellow-400 transform transition-transform hover:scale-[1.02] duration-300 relative overflow-hidden animate-slide-up">
            <div className="absolute -top-8 -left-8 w-40 h-40 bg-yellow-100 rounded-full blur-2xl opacity-70"></div>
            <div className="relative z-10">
                <h3 className="text-3xl font-extrabold text-yellow-700 flex items-center mb-6">
                <SparklesIcon className="w-12 h-12 mr-4 text-yellow-500 animate-pulse-slow" /> Power-Up Tips!
                </h3>
                <ul className="list-disc pl-10 space-y-4 text-xl text-gray-700">
                <li>Discover all the games and find your unique strengths!</li>
                <li>Short breaks keep your brain fresh and ready for more fun.</li>
                <li>Challenge your friends for epic bragging rights!</li>
                <li>Every game you play helps you learn something awesome!</li>
                </ul>
            </div>
        </section>

        {/* Achievements Section */}
        <section className="bg-white p-12 rounded-3xl shadow-2xl border-l-8 border-green-400 transform transition-transform hover:scale-[1.02] duration-300 relative overflow-hidden animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-green-100 rounded-full blur-2xl opacity-70"></div>
            <div className="relative z-10">
                <h3 className="text-3xl font-extrabold text-green-700 flex items-center mb-6">
                <TrophyIcon className="w-12 h-12 mr-4 text-green-500 animate-spin-slow" /> Your Epic Achievements!
                </h3>
                <p className="text-xl text-gray-600 italic mb-8">
                Keep playing to unlock super cool badges and earn rewards!
                </p>
                <div className="flex justify-center gap-10 mt-4">
                <div className="w-28 h-28 bg-yellow-300 rounded-full flex items-center justify-center text-6xl shadow-xl border-4 border-yellow-400 transform hover:scale-110 transition-transform duration-200 cursor-pointer animate-pop-in">
                    ⭐
                </div>
                <div className="w-28 h-28 bg-gray-200 rounded-full flex items-center justify-center text-6xl shadow-xl border-4 border-gray-300 opacity-70 transform hover:scale-110 transition-transform duration-200 cursor-pointer animate-pop-in" style={{ animationDelay: '0.1s' }}>
                    🏆
                </div>
                <div className="w-28 h-28 bg-gray-200 rounded-full flex items-center justify-center text-6xl shadow-xl border-4 border-gray-300 opacity-70 transform hover:scale-110 transition-transform duration-200 cursor-pointer animate-pop-in" style={{ animationDelay: '0.2s' }}>
                    🏅
                </div>
                </div>
            </div>
        </section>
      </div>
    </div>
  );
};

export default GamePage;